import { useCallback, useEffect, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { Center, Flex, Modal as MantineModal, Text } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { FormProvider, useForm } from "react-hook-form";
import { Modal, ModalLoading, HeaderModal } from "../../components";
import { useMutation } from "@tanstack/react-query";
import { postBuyPlan } from "../../hooks/useBuyProducts";
import PurchaseStep1 from "./PurchaseStep1";
import PurchaseStep2 from "./PurchaseStep2";
import PurchaseStep3 from "./PurchaseStep3";
import { IPostBuyPlan } from "../../interfaces/Purchase";
import { putUser } from "../../hooks/useUsers";
import { useNotificationControl } from "../../context/notificationContext";
import { IPutUser } from "../../interfaces/User";
import { useUserIP } from "../../hooks/useAuthService";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationBuyPlans } from "../../utils/validation";
import { INewUser } from "../../interfaces/User/newUser";
import { useTranslation } from "react-i18next";

const Purchase = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notificationControl } = useNotificationControl();
  const location = useLocation();
  const { returnPath, user, interest, setInterest } = useAuth();

  const { data: dataIp } = useUserIP();
  const userIp = !!dataIp?.data.ip ? dataIp?.data.ip : "";
  const [opened, { open, close }] = useDisclosure(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOpenModalError, setIsOpenModalError] = useState<string>("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);

  const getDefaultValues = (user: INewUser): IPostBuyPlan => ({
    payment_owner: {
      name: user.name,
      document_nr: user.document_nr,
      address: {
        zip_code: user?.address?.cep || "",
        city: user?.address?.city || "",
        state: user?.address?.state || "",
        additional_address: user?.address?.additional_address || "",
        country: "BR",
        number: user?.address?.number || "",
        street: user?.address?.street || "",
        neighborhood: user?.address?.neighborhood || "",
      },
    },
    payment_info: null,
    plans: [],
  });

  const selectedInterest = interest.filter((item) => item.selected);

  const methods = useForm<IPostBuyPlan>({
    mode: "all",
    resolver: yupResolver(validationBuyPlans(selectedInterest?.length)),
    defaultValues: getDefaultValues(user),
  });

  const { reset, handleSubmit, watch } = methods;

  useEffect(() => {
    if (!!user) {
      reset(getDefaultValues(user));
    }
  }, [user, reset]);

  const isFree =
    watch("plans")?.length > 0
      ? watch("plans").every((plan) => !plan.hasValue)
      : true;

  const { mutate: buyPlan } = useMutation(postBuyPlan, {
    onSuccess: () => {
      if (isNewUser) {
        setInterest((prev) => prev.filter((item) => !item.selected));
        setIsSuccess(true);
        setOpenModalConfirmation(true);
      }
    },
    onError: (errors: any) => {
      console.error(t("marketplace.comprar_serviços.error_buy"), errors);
      setIsOpenModalError(errors.response.data.message.toString() || "");
      setIsLoading(false);
    },
  });

  const { mutate: editUserAddress } = useMutation(putUser, {
    onSuccess: () => {
      notificationControl({
        message: t("marketplace.comprar_serviços.success_updated_address"),
        type: "check",
      });
    },
    onError: (errors) => {
      console.error(
        t("marketplace.comprar_serviços.error_updated_address"),
        errors
      );
      notificationControl({
        message: t("marketplace.comprar_serviços.error_updated_address"),
        type: "danger",
      });
    },
  });

  async function onSubmit(data: IPostBuyPlan) {
    const newData = isFree ? { ...data, payment_info: null } : data;
    setIsLoading(true);

    if (isNewUser) {
      buyPlan(newData);
    } else {
      try {
        const dataEditUserAddress: IPutUser = {
          id: user.id,
          cell_phone: user.cell_phone,
          name: user.name,
          document_nr: user.document_nr,
          email: user.email,
          secondary_emails: [],
          client_ip: userIp,
          address: {
            id: user?.address?.id || undefined,
            city: data.payment_owner?.address.city || "",
            state: data.payment_owner?.address.state || "",
            cep: data.payment_owner?.address.zip_code || "",
            additional_address:
              data.payment_owner?.address.additional_address || "",
            country: "BR",
            number: data.payment_owner?.address.number || "",
            street: data.payment_owner?.address.street || "",
            neighborhood: data.payment_owner?.address.neighborhood || "",
          },
          notifications: user.notifications,
        };

        await Promise.all([
          new Promise((resolve, reject) => {
            buyPlan(newData, {
              onSuccess: resolve,
              onError: reject,
            });
          }),
          new Promise((resolve, reject) => {
            editUserAddress(dataEditUserAddress, {
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ]);

        setInterest((prev) => prev.filter((item) => !item.selected));
        setIsSuccess(true);
        setOpenModalConfirmation(true);
      } catch (error) {
        console.error(t("marketplace.comprar_serviços.error"), error);
        notificationControl({
          message: t("marketplace.comprar_serviços.error"),
          type: "danger",
        });
      } finally {
        setIsLoading(false);
      }
    }
  }

  const handleRedirect = useCallback(() => {
    open();
    const from = location.pathname;
    navigate("/meu-portfolio/minhas-compras", { state: { from } });
  }, [location.pathname, navigate, open]);

  const handleOnClose = () => {
    setInterest((prev) =>
      prev.map((item) => ({
        ...item,
        toBuy: false,
        plan: item.plan.map((plan) => ({
          ...plan,
          isSelected: false,
        })),
      }))
    );
    reset();
    setIsLoading(false);
    setOpenModalConfirmation(false);
    const from = location.state?.from;
    navigate(from || "/marketplace");
    close();
  };

  const handleCloseConfirmation = () => {
    setOpenModalConfirmation(!openModalConfirmation);
  };

  const handleSucessConfirmationClose = () => {
    navigate("/marketplace");
    setIsLoading(false);
    setOpenModalConfirmation(!openModalConfirmation);
    close();
  };

  const handleChangeUser = (e: "true" | "false") => {
    if (e === "true") {
      setIsNewUser(false);
      reset({
        payment_owner: {
          name: user.name,
          document_nr: user.document_nr,
          address: {
            zip_code: user?.address?.cep || "",
            city: user?.address?.city || "",
            state: user?.address?.state || "",
            additional_address: user?.address?.additional_address || "",
            country: "BR",
            number: user?.address?.number || undefined,
            street: user?.address?.street || "",
            neighborhood: user?.address?.neighborhood || "",
          },
        },
      });
    } else {
      setIsNewUser(true);
      reset({
        payment_owner: {
          name: "",
          document_nr: "",
          address: {
            zip_code: "",
            city: "",
            state: "",
            additional_address: "",
            country: "BR",
            number: undefined,
            street: "",
            neighborhood: "",
          },
        },
      });
    }
  };
  const handleCloseErrorModal = () => {
    setIsOpenModalError("");
  };

  return (
    <>
      {isLoading ? (
        <ModalLoading open={isLoading} />
      ) : (
        <Modal
          type={isSuccess ? "success" : "warning"}
          titleFirstButton={
            isSuccess
              ? t(
                  "marketplace.comprar_serviços.modal_success.title_first_button"
                )
              : t(
                  "marketplace.comprar_serviços.modal_cancel.title_first_button"
                )
          }
          titleSeccondButton={
            isSuccess
              ? t("global.button.close")
              : t(
                  "marketplace.comprar_serviços.modal_cancel.title_seccond_button"
                )
          }
          description={
            isSuccess
              ? t("marketplace.comprar_serviços.modal_success.description")
              : t("marketplace.comprar_serviços.modal_cancel.description")
          }
          open={openModalConfirmation}
          setClose={
            isSuccess ? handleSucessConfirmationClose : handleCloseConfirmation
          }
          secondClick={
            isSuccess ? handleSucessConfirmationClose : handleCloseConfirmation
          }
          firstClick={isSuccess ? handleRedirect : handleOnClose}
        />
      )}

      <Modal
        type={"danger"}
        titleFirstButton={t("global.button.close")}
        description={isOpenModalError}
        contentEspecial={
          <Text c="basic.4" fz="16px" lh="155%" ta={"center"} mb="32px">
            {t("marketplace.comprar_serviços.modal_danger.content_especial")}
          </Text>
        }
        textModal={t("marketplace.comprar_serviços.modal_danger.text_modal")}
        open={!!isOpenModalError}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
      />

      <MantineModal
        yOffset={0}
        withCloseButton={false}
        opened={opened}
        onClose={handleOnClose}
        fullScreen
        transitionProps={{ transition: "slide-up", duration: 200 }}
        styles={{
          body: { padding: 0 },
          content: {
            background: "other.0",
          },
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <HeaderModal
              title={t("marketplace.comprar_serviços.title")}
              isLoading={isLoading}
              setCancelRegister={() =>
                setOpenModalConfirmation(!openModalConfirmation)
              }
            />
            <Center bg="other.0">
              <Flex
                justify="center"
                direction="column"
                maw={1920}
                p={{ base: "24px 24px", 991: "48px 48px", 1600: "48px 120px" }}
                w="100%"
                gap="80px"
              >
                <PurchaseStep1
                  handleChangeUser={handleChangeUser}
                  isNewUser={isNewUser}
                />
                <PurchaseStep2 />
                <PurchaseStep3 isLoading={isLoading} isFree={isFree} />
              </Flex>
            </Center>
          </form>
        </FormProvider>
      </MantineModal>
    </>
  );
};
export default Purchase;
